/**
 * 17-point audit report generator.
 *
 * Builds a self-contained HTML report and opens it in a new tab. The new
 * tab includes:
 *   - Print / Save-as-PDF button (uses window.print() — best browser PDF)
 *   - Download HTML button (offers the report as a standalone .html file)
 *   - Polished print CSS so saved PDF looks audit-grade
 *   - All data inlined — works offline once opened
 *
 * The report is structured for:
 *   1. Auditor review (on-screen scan, click anything to read)
 *   2. PDF archival (print-optimized layout, page breaks at section boundaries)
 *   3. Sharing with leadership / regulator (professional cover page,
 *      executive summary, then drill-down)
 */

import type { FindingDTO, LosApplicationDataDTO, BankingVerificationDTO } from '@loanaudit/shared';

// Minimal loan-file shape the report needs. Matches LoanFileDetail
// (frontend) but locally typed to avoid coupling the report module
// to the ingestion api shape directly.
interface ReportLoanFile {
  id: string;
  loanNumber: string;
  borrowerName: string;
  productCode: string;
  branchCode: string;
  state: string;
}

// -------------------------------------------------------------------------
// Checklist row metadata — mirrors ChecklistGrid.tsx exactly. Kept inline
// here so the report module is self-contained.
// -------------------------------------------------------------------------

interface ChecklistRowMeta {
  point: number;
  label: string;
  description: string;
  ruleIds: readonly string[];
  losPath: string;
}

const CHECKLIST_ROWS: readonly ChecklistRowMeta[] = [
  { point: 1, label: 'Documents Required',
    description: 'Min 2 KYC, CAM, CFA, BQS report, Legal & Technical report',
    ruleIds: ['los.documents_required', 'uc1.mandatory_doc.kyc'],
    losPath: 'LOS → Document → Digi Document Checklist' },
  { point: 2, label: 'API Status Details',
    description: 'PAN, Aadhar, eMandate, CKYC, PAN Profile all Success+Validated',
    ruleIds: ['los.api_statuses_validated'],
    losPath: 'LOS → Audit → API Status Details' },
  { point: 3, label: 'Dedupe Screening',
    description: 'Posidex Validated for every applicant',
    ruleIds: ['los.dedupe_all_applicants_validated'],
    losPath: 'LOS → Application Details → View Dedupe Review' },
  { point: 4, label: 'CIBIL Score',
    description: 'Meets product-specific threshold (PL 740, Disha HL 651, etc.)',
    ruleIds: ['los.cibil_score_meets_threshold'],
    losPath: 'LOS → Decision → CIBIL Report View' },
  { point: 5, label: 'Hunter Verification',
    description: 'Clean — or approved deviation on file',
    ruleIds: ['los.hunter_clean_or_deviation'],
    losPath: 'LOS → Audit → Hunter Review View' },
  { point: 6, label: 'BQS Check',
    description: 'No Match — or approved deviation on file',
    ruleIds: ['los.bqs_no_match_or_deviation'],
    losPath: 'LOS → Underwriting → Recommended/Final Decision View' },
  { point: 7, label: 'RCU Status',
    description: 'Positive outcome — or approved deviation on file',
    ruleIds: ['los.rcu_status_valid'],
    losPath: 'LOS → Verification → RCU Verification View' },
  { point: 8, label: 'Income Proof (PL/BL)',
    description: 'Salary credits verified via Novel Check banking',
    ruleIds: ['los.novel_check_performed'],
    losPath: 'LOS → Document → Banking Analysis' },
  { point: 9, label: 'Income Proof (ML/HL)',
    description: 'Banking surrogate / GST program — CFA verified',
    ruleIds: ['los.novel_check_performed'],
    losPath: 'LOS → Verification → Cash Flow Analysis' },
  { point: 10, label: 'Novel Check',
    description: 'Banking analysis performed',
    ruleIds: ['los.novel_check_performed'],
    losPath: 'LOS → Document → Banking Analysis' },
  { point: 11, label: 'Loan Eligibility',
    description: 'Amount, ROI, tenor, EMI match CAM',
    ruleIds: ['los.loan_eligibility_matches_cam'],
    losPath: 'LOS → Detailed Data Entry → Loan Details' },
  { point: 12, label: 'Company Cap',
    description: 'Sanctioned ≤ company-category cap',
    ruleIds: ['los.loan_within_company_cap'],
    losPath: 'LOS → Detailed Data Entry → Applicant → Company Categorization' },
  { point: 13, label: 'Deviation Matrix',
    description: 'All deviations approved with documented mitigants',
    ruleIds: ['los.all_deviations_approved'],
    losPath: 'LOS → Deviation → Approve Deviation View' },
  { point: 14, label: 'Validity of Sanction',
    description: 'Disbursement within 30 days of sanction',
    ruleIds: ['los.disbursement_within_validity'],
    losPath: 'LOS → Audit → Activity History' },
  { point: 15, label: 'Sanction Conditions',
    description: 'Conditions documented in LOS',
    ruleIds: ['los.sanction_conditions_match_cam'],
    losPath: 'LOS → Underwriting → Sanction Condition View' },
  { point: 16, label: 'Loan Agreement',
    description: 'E-Agreement Success + all metadata fields populated',
    ruleIds: ['los.e_agreement_metadata_complete'],
    losPath: 'LOS → WF Activities → Initiate E-Agreement View' },
  { point: 17, label: 'Key Fact Statement',
    description: 'KFS present alongside Loan Agreement',
    ruleIds: ['los.kfs_attached_to_loan_agreement'],
    losPath: 'LOS → WF Activities → Initiate E-Agreement View' },
] as const;

type RowStatus = 'passed' | 'justified' | 'failed' | 'inconclusive' | 'no_los';

interface DerivedRow extends ChecklistRowMeta {
  status: RowStatus;
  finding: FindingDTO | null;
}

function deriveStatus(
  row: ChecklistRowMeta,
  findingsByRuleId: Map<string, FindingDTO>,
  hasLosData: boolean,
): DerivedRow {
  for (const ruleId of row.ruleIds) {
    const f = findingsByRuleId.get(ruleId);
    if (f) {
      const titleLower = (f.title ?? '').toLowerCase();
      if (titleLower.includes('deviation approved')) {
        return { ...row, status: 'justified', finding: f };
      }
      if (!f.severity) {
        return { ...row, status: 'inconclusive', finding: f };
      }
      return { ...row, status: 'failed', finding: f };
    }
  }
  if (!hasLosData && row.ruleIds.some((r) => r.startsWith('los.'))) {
    return { ...row, status: 'no_los', finding: null };
  }
  return { ...row, status: 'passed', finding: null };
}

// -------------------------------------------------------------------------
// HTML escaping — every interpolated string MUST go through this. The
// alternative is XSS via crafted LOS fields or finding descriptions.
// -------------------------------------------------------------------------

/** Shown where the LOS API simply doesn't carry the section — never a pass/fail. */
const NOT_REPORTED = 'Not reported by LOS';

function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const formatInr = (n: number | null | undefined): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return '₹' + n.toLocaleString('en-IN');
};

const formatPct = (n: number | null | undefined): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n}%`;
};

// -------------------------------------------------------------------------
// Public entrypoint
// -------------------------------------------------------------------------

export interface GenerateReportInput {
  loanFile: ReportLoanFile;
  findings: FindingDTO[];
  losData: LosApplicationDataDTO | null;
  /** Validation run timestamp ISO string, for the report footer. */
  validatedAt?: string | null;
  /**
   * Latest completed banking verification (Chunk 3).
   * When present, the report includes a "Banking Verification Evidence"
   * section with independent bank statement analysis results.
   */
  bankingVerification?: BankingVerificationDTO | null;
}

export function generateAndOpenReport(input: GenerateReportInput): void {
  const html = buildReportHtml(input);
  // Use a blob URL so we get a real navigable URL (better than data: URLs
  // for in-tab actions like print and download).
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener');
  if (!tab) {
    // Popup blocked — fall back to a forced download of the HTML file.
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-report-${input.loanFile.loanNumber}.html`;
    a.click();
  }
  // Revoke later so the new tab has time to load.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// -------------------------------------------------------------------------
// HTML builder
// -------------------------------------------------------------------------

function buildReportHtml(input: GenerateReportInput): string {
  const { loanFile, findings, losData, validatedAt, bankingVerification } = input;

  const findingsByRuleId = new Map<string, FindingDTO>();
  for (const f of findings) {
    if (!findingsByRuleId.has(f.ruleId)) findingsByRuleId.set(f.ruleId, f);
  }
  const hasLosData = losData !== null;
  const rows = CHECKLIST_ROWS.map((r) => deriveStatus(r, findingsByRuleId, hasLosData));

  // Tallies
  const passed = rows.filter((r) => r.status === 'passed').length;
  const justified = rows.filter((r) => r.status === 'justified').length;
  const failed = rows.filter((r) => r.status === 'failed').length;
  const inconclusive = rows.filter((r) => r.status === 'inconclusive').length;
  const noLos = rows.filter((r) => r.status === 'no_los').length;
  const evaluable = passed + justified + failed;
  const score = evaluable === 0 ? 0 : Math.round(((passed + justified) / evaluable) * 100);

  // Score tier — green if ≥90 and zero failures, amber 70-89 or any failure, red <70
  const tier: 'green' | 'amber' | 'red' =
    failed > 0
      ? (score >= 70 ? 'amber' : 'red')
      : (score >= 90 ? 'green' : score >= 70 ? 'amber' : 'red');

  const verdict =
    failed === 0 && noLos === 0 && inconclusive <= 2
      ? 'Audit clearance — eligible for disbursement'
      : failed > 0
        ? 'Not eligible for disbursement — remediation required'
        : 'Inconclusive — data missing, cannot certify';

  const verdictTone =
    failed > 0 ? 'red' : (noLos > 0 || inconclusive > 0 ? 'amber' : 'green');

  const generatedAtStr = new Date().toLocaleString('en-IN', {
    dateStyle: 'long', timeStyle: 'medium',
  });
  const validatedAtStr = validatedAt
    ? new Date(validatedAt).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'medium' })
    : '—';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Audit Report — ${escapeHtml(loanFile.loanNumber)}</title>
<style>
${REPORT_CSS}
</style>
</head>
<body>
${renderToolbar(loanFile.loanNumber)}

<main class="report">
  ${renderCoverPage({
    loanFile, losData, score, tier, verdict, verdictTone,
    passed, justified, failed, inconclusive, noLos,
    generatedAtStr, validatedAtStr,
  })}

  ${renderChecklistTable(rows)}

  ${renderFailedFindings(rows)}

  ${renderBankingVerificationSection(bankingVerification ?? null)}

  ${renderDeviations(losData)}

  ${renderLosSummary(losData)}

  ${renderFooter(loanFile.loanNumber)}
</main>

<script>
${REPORT_JS}
</script>
</body>
</html>`;
}

// -------------------------------------------------------------------------
// Toolbar (non-printing)
// -------------------------------------------------------------------------

function renderToolbar(loanNumber: string): string {
  return `
<header class="toolbar no-print">
  <div class="toolbar-left">
    <strong>Audit Report</strong>
    <span class="muted">${escapeHtml(loanNumber)}</span>
  </div>
  <div class="toolbar-actions">
    <button id="btn-print" class="btn btn-primary" type="button">
      🖨️ &nbsp;Print / Save as PDF
    </button>
    <button id="btn-download-html" class="btn" type="button">
      ⬇️ &nbsp;Download HTML
    </button>
    <button id="btn-close" class="btn btn-ghost" type="button">
      ✕ Close
    </button>
  </div>
</header>
<div class="toolbar-hint no-print">
  Tip: Use <kbd>Print</kbd> → <kbd>Save as PDF</kbd> in your browser for a clean PDF copy.
</div>
`;
}

// -------------------------------------------------------------------------
// Cover page — score + verdict + headline data
// -------------------------------------------------------------------------

interface CoverArgs {
  loanFile: ReportLoanFile;
  losData: LosApplicationDataDTO | null;
  score: number;
  tier: 'green' | 'amber' | 'red';
  verdict: string;
  verdictTone: 'green' | 'amber' | 'red';
  passed: number;
  justified: number;
  failed: number;
  inconclusive: number;
  noLos: number;
  generatedAtStr: string;
  validatedAtStr: string;
}

function renderCoverPage(a: CoverArgs): string {
  const sanctioned = a.losData?.data?.loanDetails?.sanctionedAmount ?? null;
  const interestRate = a.losData?.data?.loanDetails?.interestRatePct ?? null;
  const tenure = a.losData?.data?.loanDetails?.tenureMonths ?? null;
  const emi = a.losData?.data?.loanDetails?.emiAmount ?? null;
  const sanctionDate = a.losData?.data?.dates?.sanctionDate ?? null;
  const disbursementDate = a.losData?.data?.dates?.disbursementDate ?? null;
  const officerId = a.losData?.data?.officerId ?? null;

  return `
<section class="cover">

  <div class="brand-bar">
    <div class="brand-mark">AXIS LOAN AUDIT</div>
    <div class="brand-sub">Agentic 17-Point Compliance Audit Report</div>
  </div>

  <h1 class="cover-title">Loan Audit Report</h1>
  <div class="cover-loan-id">
    Loan ${escapeHtml(a.loanFile.loanNumber)}
  </div>

  <div class="cover-grid">
    <div class="cover-card">
      <div class="card-label">Borrower</div>
      <div class="card-value">${escapeHtml(a.loanFile.borrowerName || '—')}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">Product</div>
      <div class="card-value">${escapeHtml(a.loanFile.productCode || '—')}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">Branch</div>
      <div class="card-value">${escapeHtml(a.loanFile.branchCode || '—')}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">Officer</div>
      <div class="card-value">${escapeHtml(officerId || '—')}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">Sanctioned Amount</div>
      <div class="card-value">${escapeHtml(formatInr(sanctioned))}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">Interest Rate</div>
      <div class="card-value">${escapeHtml(formatPct(interestRate))}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">Tenure</div>
      <div class="card-value">${tenure ? escapeHtml(tenure) + ' months' : '—'}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">EMI</div>
      <div class="card-value">${escapeHtml(formatInr(emi))}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">Sanction Date</div>
      <div class="card-value">${escapeHtml(sanctionDate || '—')}</div>
    </div>
    <div class="cover-card">
      <div class="card-label">Disbursement Date</div>
      <div class="card-value">${escapeHtml(disbursementDate || '—')}</div>
    </div>
  </div>

  <div class="score-block tier-${a.tier}">
    <div class="score-circle">
      <div class="score-value">${a.score}</div>
      <div class="score-max">/ 100</div>
    </div>
    <div class="score-side">
      <div class="score-heading">Compliance Score</div>
      <div class="score-stats">
        <span class="stat-chip chip-green">✓ ${a.passed} passed</span>
        ${a.justified > 0 ? `<span class="stat-chip chip-cyan">⚖ ${a.justified} deviation-justified</span>` : ''}
        ${a.failed > 0 ? `<span class="stat-chip chip-red">✗ ${a.failed} failed</span>` : ''}
        ${a.inconclusive + a.noLos > 0 ? `<span class="stat-chip chip-amber">◐ ${a.inconclusive + a.noLos} inconclusive</span>` : ''}
      </div>
    </div>
  </div>

  <div class="verdict verdict-${a.verdictTone}">
    <div class="verdict-label">Audit Verdict</div>
    <div class="verdict-text">${escapeHtml(a.verdict)}</div>
  </div>

  <div class="meta-row">
    <div><span class="meta-label">Validated:</span> ${escapeHtml(a.validatedAtStr)}</div>
    <div><span class="meta-label">Report generated:</span> ${escapeHtml(a.generatedAtStr)}</div>
  </div>
</section>
`;
}

// -------------------------------------------------------------------------
// Checklist table — all 17 rows
// -------------------------------------------------------------------------

const STATUS_META: Record<RowStatus, { label: string; cls: string }> = {
  passed:       { label: 'Passed',              cls: 'badge-green' },
  justified:    { label: 'Deviation justified', cls: 'badge-cyan' },
  failed:       { label: 'Failed',              cls: 'badge-red' },
  inconclusive: { label: 'Inconclusive',        cls: 'badge-amber' },
  no_los:       { label: 'No LOS data',         cls: 'badge-grey' },
};

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info',
};

function renderChecklistTable(rows: DerivedRow[]): string {
  const rowsHtml = rows
    .map((r) => {
      const meta = STATUS_META[r.status];
      const sev = r.finding?.severity ? SEVERITY_LABEL[r.finding.severity] ?? r.finding.severity : '—';
      // Description: use finding's own description for failed/justified/inconclusive, default otherwise
      const description = r.finding?.description || r.description;
      return `
<tr class="row-${r.status}">
  <td class="point-num">${r.point}</td>
  <td>
    <div class="check-label">${escapeHtml(r.label)}</div>
    <div class="check-desc">${escapeHtml(description)}</div>
  </td>
  <td><span class="badge ${meta.cls}">${escapeHtml(meta.label)}</span></td>
  <td class="severity-cell">${escapeHtml(sev)}</td>
  <td class="los-path">${escapeHtml(r.losPath)}</td>
</tr>
`;
    })
    .join('');

  return `
<section class="section section-checklist">
  <h2>17-Point Checklist Results</h2>
  <table class="checklist-table">
    <thead>
      <tr>
        <th style="width:48px">#</th>
        <th>Check</th>
        <th style="width:160px">Status</th>
        <th style="width:80px">Severity</th>
        <th style="width:280px">LOS Source</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
  </table>
</section>
`;
}

// -------------------------------------------------------------------------
// Failed findings detail
// -------------------------------------------------------------------------

function renderFailedFindings(rows: DerivedRow[]): string {
  const failedRows = rows.filter((r) => r.status === 'failed' && r.finding);
  if (failedRows.length === 0) {
    return `
<section class="section">
  <h2>Failed Findings — Detail</h2>
  <div class="empty-state">
    ✓ No failed checks on this loan file. Either all checks passed or any
    detected issues have approved deviations on file.
  </div>
</section>
`;
  }

  const itemsHtml = failedRows
    .map((r) => {
      const f = r.finding!;
      const sev = f.severity ? SEVERITY_LABEL[f.severity] ?? f.severity : '—';
      return `
<article class="finding">
  <header class="finding-header">
    <div class="finding-point">Point ${r.point}</div>
    <div class="finding-title">${escapeHtml(f.title || r.label)}</div>
    <div class="finding-meta">
      <span class="badge badge-red">${escapeHtml(sev)}</span>
      <span class="rule-id">${escapeHtml(f.ruleId)}</span>
    </div>
  </header>
  <div class="finding-body">
    <div class="finding-section">
      <div class="finding-section-label">Description</div>
      <div>${escapeHtml(f.description)}</div>
    </div>
    <div class="finding-section">
      <div class="finding-section-label">LOS source</div>
      <div class="los-path-inline">${escapeHtml(r.losPath)}</div>
    </div>
    ${renderEvidenceBlock(f)}
  </div>
</article>
`;
    })
    .join('');

  return `
<section class="section page-break">
  <h2>Failed Findings — Detail</h2>
  <div class="section-intro">
    The following ${failedRows.length} check(s) failed. Each entry includes evidence
    and the LOS screen path the auditor should consult to verify or remediate.
  </div>
  ${itemsHtml}
</section>
`;
}

function renderEvidenceBlock(f: FindingDTO): string {
  const ev = f.evidence;
  if (!ev) return '';
  const blocks: string[] = [];

  if (ev.description) {
    blocks.push(`
<div class="finding-section">
  <div class="finding-section-label">Evidence</div>
  <div>${escapeHtml(ev.description)}</div>
</div>
`);
  }
  if (ev.fields && ev.fields.length > 0) {
    blocks.push(`
<div class="finding-section">
  <div class="finding-section-label">Field-level evidence</div>
  <table class="kv-table">
    <thead><tr><th>Field</th><th>Value</th></tr></thead>
    <tbody>
      ${ev.fields.slice(0, 12).map((f2) => `
        <tr>
          <td class="mono">${escapeHtml(f2.fieldPath)}</td>
          <td class="mono">${escapeHtml(JSON.stringify(f2.value))}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
</div>
`);
  }
  const extra = (ev as unknown as { extra?: Record<string, unknown> }).extra;
  if (extra && typeof extra === 'object') {
    const items: string[] = [];
    for (const [k, v] of Object.entries(extra).slice(0, 10)) {
      if (k === 'checklistPoint') continue;
      items.push(`
        <tr>
          <td class="mono">${escapeHtml(k)}</td>
          <td class="mono pre">${escapeHtml(typeof v === 'string' ? v : JSON.stringify(v, null, 2))}</td>
        </tr>
      `);
    }
    if (items.length > 0) {
      blocks.push(`
<div class="finding-section">
  <div class="finding-section-label">Additional context</div>
  <table class="kv-table">
    <tbody>${items.join('')}</tbody>
  </table>
</div>
`);
    }
  }
  return blocks.join('');
}

// -------------------------------------------------------------------------
// Deviations register
// -------------------------------------------------------------------------

function renderDeviations(losData: LosApplicationDataDTO | null): string {
  const list = losData?.data?.deviations ?? [];
  if (list.length === 0) {
    return `
<section class="section">
  <h2>Deviations Register</h2>
  <div class="empty-state">No deviations recorded for this loan file.</div>
</section>
`;
  }

  const rowsHtml = list
    .map((d, i) => `
<tr>
  <td class="point-num">${i + 1}</td>
  <td class="mono">${escapeHtml(d.deviationType)}</td>
  <td>
    <span class="badge ${d.status === 'approved' ? 'badge-green' : d.status === 'pending' ? 'badge-amber' : 'badge-red'}">
      ${escapeHtml(d.status)}
    </span>
  </td>
  <td>${escapeHtml(d.approverId || '—')}</td>
  <td>${escapeHtml(d.mitigant || '—')}</td>
</tr>
`)
    .join('');

  return `
<section class="section page-break">
  <h2>Deviations Register</h2>
  <div class="section-intro">
    Deviations on file, with approver and mitigant. An approved deviation
    with a documented mitigant justifies the corresponding check on the
    17-point grid.
  </div>
  <table class="checklist-table">
    <thead>
      <tr>
        <th style="width:36px">#</th>
        <th style="width:200px">Type</th>
        <th style="width:90px">Status</th>
        <th style="width:120px">Approver</th>
        <th>Mitigant</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>
</section>
`;
}

// -------------------------------------------------------------------------
// LOS data summary — applicants + key data
// -------------------------------------------------------------------------

function renderLosSummary(losData: LosApplicationDataDTO | null): string {
  if (!losData) {
    return `
<section class="section">
  <h2>LOS Data</h2>
  <div class="empty-state empty-warning">
    No LOS data attached to this loan file. The 17-point checklist could not
    be fully evaluated. Upload the LOS export JSON to complete the audit.
  </div>
</section>
`;
  }
  const d = losData.data;

  const applicants = (d.applicants || []).map((a, i) => `
<tr>
  <td class="point-num">${i + 1}</td>
  <td>${escapeHtml(a.name)}</td>
  <td class="mono">${escapeHtml(a.role)}</td>
  <td class="mono">${escapeHtml(a.pan || '—')}</td>
  <td>${escapeHtml(a.posidexStatus)}</td>
  <td>${a.cibilScore === null ? '—' : escapeHtml(a.cibilScore)}</td>
  <td>${escapeHtml((a.kycDocuments || []).join(', ') || '—')}</td>
</tr>
`).join('');

  const conditions = (d.sanctionConditions || []).map((c) =>
    `<li>${escapeHtml(c)}</li>`
  ).join('');

  const api = d.apiStatusDetails;
  const apiRows = [
    ['PAN Card',    api?.panCard],
    ['Aadhar',      api?.aadhar],
    ['eMandate',    api?.eMandate],
    ['CKYC',        api?.ckyc],
    ['PAN Profile', api?.panProfile],
  ].map(([name, entry]) => {
    const e = entry as { status?: string; validated?: boolean } | undefined;
    return `
<tr>
  <td>${escapeHtml(name as string)}</td>
  <td>
    ${e
      ? `<span class="badge ${e.status === 'Success' ? 'badge-green' : 'badge-red'}">${escapeHtml(e.status || '—')}</span>`
      : NOT_REPORTED}
  </td>
  <td>${e ? (e.validated ? '✓' : '✗') : '—'}</td>
</tr>
`;
  }).join('');

  return `
<section class="section page-break">
  <h2>LOS Data Summary</h2>

  <h3>Applicants (${d.applicants.length})</h3>
  <table class="checklist-table">
    <thead>
      <tr>
        <th style="width:30px">#</th>
        <th>Name</th>
        <th style="width:100px">Role</th>
        <th style="width:110px">PAN</th>
        <th style="width:110px">Posidex</th>
        <th style="width:70px">CIBIL</th>
        <th>KYC Docs</th>
      </tr>
    </thead>
    <tbody>${applicants}</tbody>
  </table>

  <h3>Workflow Checks</h3>
  <div class="workflow-grid">
    <div class="workflow-tile">
      <div class="tile-label">Hunter</div>
      <div class="tile-value">${d.hunterVerification ? escapeHtml(d.hunterVerification.result) : NOT_REPORTED}</div>
      <div class="tile-sub">${d.hunterVerification ? (d.hunterVerification.matchFound ? 'Match found' : 'No match') : ''}</div>
    </div>
    <div class="workflow-tile">
      <div class="tile-label">BQS</div>
      <div class="tile-value">${d.bqsCheck ? escapeHtml(d.bqsCheck.result) : NOT_REPORTED}</div>
    </div>
    <div class="workflow-tile">
      <div class="tile-label">RCU</div>
      <div class="tile-value">${d.rcuVerification ? escapeHtml(d.rcuVerification.outcome) : NOT_REPORTED}</div>
      <div class="tile-sub">${d.rcuVerification ? escapeHtml(d.rcuVerification.type) : ''}</div>
    </div>
    <div class="workflow-tile">
      <div class="tile-label">Novel Check</div>
      <div class="tile-value">${d.novelCheckBanking ? (d.novelCheckBanking.performed ? 'Performed' : 'Not done') : NOT_REPORTED}</div>
      <div class="tile-sub">${d.novelCheckBanking ? (d.novelCheckBanking.salaryCreditsVerified ? 'Salary verified' : 'Not verified') : ''}</div>
    </div>
    <div class="workflow-tile">
      <div class="tile-label">E-Agreement</div>
      <div class="tile-value">${d.eAgreement ? escapeHtml(d.eAgreement.interfaceStatus) : NOT_REPORTED}</div>
      <div class="tile-sub">${d.eAgreement ? escapeHtml(d.eAgreement.legalDocId || 'no doc id') : ''}</div>
    </div>
    <div class="workflow-tile">
      <div class="tile-label">Company Cat.</div>
      <div class="tile-value">${d.companyCategorization ? escapeHtml(d.companyCategorization.category) : NOT_REPORTED}</div>
      <div class="tile-sub">${d.companyCategorization ? escapeHtml(d.companyCategorization.employerName) : ''}</div>
    </div>
  </div>

  <h3>API Status Details</h3>
  <table class="checklist-table" style="max-width: 500px">
    <thead>
      <tr><th>API</th><th>Status</th><th style="width:90px">Validated</th></tr>
    </thead>
    <tbody>${apiRows}</tbody>
  </table>

  ${conditions ? `
  <h3>Sanction Conditions</h3>
  <ul class="conditions">${conditions}</ul>
  ` : ''}
</section>
`;
}

// -------------------------------------------------------------------------
// Footer
// -------------------------------------------------------------------------

function renderBankingVerificationSection(bv: BankingVerificationDTO | null): string {
  if (!bv || bv.status !== 'completed' || !bv.response) {
    // Show a minimal informational block if no verification has run.
    return `
<section class="section page-break">
  <div class="section-title">Banking Verification Evidence</div>
  <div class="section-intro">
    No independent banking verification has been run for this loan file.
    Upload a bank statement PDF and trigger Novel Check verification from the
    loan file detail page to include independent analysis in this report.
  </div>
</section>`;
  }

  const r = bv.response;
  const pct = (v: number) => `${Math.round(v * 100)}%`;

  const overallStatus = r.editedPdfDetected
    ? '<span style="color:#dc2626;font-weight:600">⚠ TAMPER FLAG</span>'
    : r.salaryCreditsFound === 0
      ? '<span style="color:#dc2626;font-weight:600">✗ No salary credits found</span>'
      : !r.salaryEmployerMatch
        ? '<span style="color:#d97706;font-weight:600">⚠ Employer mismatch</span>'
        : '<span style="color:#16a34a;font-weight:600">✓ Passed</span>';

  const monthsHtml = r.perMonthBreakdown.length > 0
    ? `
<table class="data-table">
  <thead>
    <tr>
      <th>Month</th>
      <th style="text-align:right">Salary Credits</th>
      <th style="text-align:right">Total Amount</th>
      <th style="text-align:right">Total Credits</th>
      <th style="text-align:right">End Balance</th>
    </tr>
  </thead>
  <tbody>
    ${r.perMonthBreakdown.map((m) => `
    <tr>
      <td><code>${escapeHtml(m.month)}</code></td>
      <td style="text-align:right">${m.salaryCredits.length}</td>
      <td style="text-align:right">${formatInr(m.salaryCredits.reduce((s: number, c: number) => s + c, 0))}</td>
      <td style="text-align:right">${formatInr(m.totalCreditsInr)}</td>
      <td style="text-align:right">${formatInr(m.endBalanceInr)}</td>
    </tr>`).join('')}
  </tbody>
</table>`
    : '<p style="color:#64748b;font-style:italic">No per-month breakdown available.</p>';

  const anomaliesHtml = r.anomalies.length > 0
    ? `<div style="margin-top:8px"><strong>Anomaly flags:</strong>
        <ul style="margin:4px 0 0 0;padding-left:20px">
          ${r.anomalies.map((a: string) => `<li style="color:#92400e">${escapeHtml(a)}</li>`).join('')}
        </ul></div>`
    : '';

  return `
<section class="section page-break">
  <div class="section-title">Banking Verification Evidence</div>
  <div class="section-intro">
    Independent bank statement analysis via Novel Check (provider: <strong>${escapeHtml(bv.provider)}</strong>).
    This section presents the independently verified banking data alongside the LOS-declared Novel Check flags.
    The audit rules for checklist points 8, 9, and 10 are enriched by this data.
  </div>

  <div class="kv-grid" style="display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin-bottom:16px">
    <div class="kv-item"><div class="kv-label">Overall Status</div><div class="kv-value">${overallStatus}</div></div>
    <div class="kv-item"><div class="kv-label">Provider</div><div class="kv-value">${escapeHtml(bv.provider)}</div></div>
    <div class="kv-item"><div class="kv-label">Salary Credits Found</div><div class="kv-value">${r.salaryCreditsFound}</div></div>
    <div class="kv-item"><div class="kv-label">Months Covered</div><div class="kv-value">${r.monthsCovered}</div></div>
    <div class="kv-item"><div class="kv-label">Monthly Avg Balance</div><div class="kv-value">${formatInr(r.monthlyAverageBalanceInr)}</div></div>
    <div class="kv-item"><div class="kv-label">Avg Monthly Credit</div><div class="kv-value">${formatInr(r.averageMonthlyCreditInr)}</div></div>
    <div class="kv-item"><div class="kv-label">Observed Salary Avg</div><div class="kv-value">${formatInr(r.observedSalaryAvgInr)}</div></div>
    <div class="kv-item"><div class="kv-label">Employer Match</div>
      <div class="kv-value">${r.salaryEmployerMatch
        ? `<span style="color:#16a34a">Yes</span> (${pct(r.salaryEmployerMatchConfidence)} confidence)`
        : `<span style="color:#dc2626">No</span> (${pct(r.salaryEmployerMatchConfidence)} confidence)`}</div></div>
    <div class="kv-item"><div class="kv-label">PDF Tamper Flag</div>
      <div class="kv-value">${r.editedPdfDetected
        ? '<span style="color:#dc2626;font-weight:600">⚠ Editing detected</span>'
        : '<span style="color:#16a34a">Clean</span>'}</div></div>
    <div class="kv-item"><div class="kv-label">Verified At</div><div class="kv-value">${bv.completedAt ? new Date(bv.completedAt).toLocaleString() : '—'}</div></div>
  </div>

  ${anomaliesHtml}

  <div style="margin-top:16px">
    <div class="subsection-title">Month-by-Month Breakdown</div>
    ${monthsHtml}
  </div>

  <div style="margin-top:12px;padding:10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;font-size:11px;color:#64748b">
    Provider job ID: <code>${escapeHtml(r.providerJobId)}</code> · Verification ID: <code>${escapeHtml(bv.id)}</code>
  </div>
</section>`;
}

function renderFooter(loanNumber: string): string {
  return `
<footer class="report-footer">
  <div>Generated by Axis Loan Audit Platform · ${escapeHtml(loanNumber)}</div>
  <div class="small">
    This is a system-generated audit report. The 17-point checklist follows
    Axis Finance audit policy. Findings are subject to auditor review and
    final disposition (accept / waive / false-positive).
  </div>
</footer>
`;
}

// -------------------------------------------------------------------------
// CSS — print-aware, professional typography
// -------------------------------------------------------------------------

export const REPORT_CSS = `
:root {
  --primary: #1e3a8a;
  --accent: #0891b2;
  --text: #0f172a;
  --muted: #475569;
  --light: #f1f5f9;
  --border: #cbd5e1;
  --green-bg: #d1fae5;
  --green-text: #065f46;
  --amber-bg: #fef3c7;
  --amber-text: #92400e;
  --red-bg: #fee2e2;
  --red-text: #991b1b;
  --cyan-bg: #cffafe;
  --cyan-text: #0e7490;
  --grey-bg: #e2e8f0;
  --grey-text: #475569;
}

* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #e2e8f0; }
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: var(--text);
  font-size: 13px;
  line-height: 1.5;
}

/* ---------- Toolbar (screen only) ---------- */
.toolbar {
  background: var(--primary);
  color: white;
  padding: 14px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
}
.toolbar-left { display: flex; gap: 12px; align-items: baseline; }
.toolbar-left strong { font-size: 16px; }
.toolbar-left .muted { color: rgba(255,255,255,0.7); font-family: 'SF Mono', Monaco, monospace; }
.toolbar-actions { display: flex; gap: 8px; }
.btn {
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  padding: 7px 14px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.1);
  color: white;
  cursor: pointer;
}
.btn:hover { background: rgba(255,255,255,0.2); }
.btn-primary { background: white; color: var(--primary); border-color: white; }
.btn-primary:hover { background: #f1f5f9; }
.btn-ghost { background: transparent; }
.toolbar-hint {
  background: #fef9c3;
  color: #713f12;
  padding: 6px 24px;
  font-size: 12px;
  border-bottom: 1px solid #fde047;
}
kbd {
  background: #fff;
  border: 1px solid #d4b88a;
  border-radius: 3px;
  padding: 1px 5px;
  font-family: inherit;
  font-size: 11px;
}

/* ---------- Page layout ---------- */
.report {
  max-width: 880px;
  margin: 20px auto;
  background: white;
  padding: 48px 56px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
}

/* ---------- Cover ---------- */
.brand-bar {
  border-bottom: 3px solid var(--primary);
  padding-bottom: 8px;
  margin-bottom: 24px;
}
.brand-mark {
  font-size: 11px;
  letter-spacing: 2px;
  color: var(--primary);
  font-weight: 700;
}
.brand-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}
.cover-title {
  font-size: 32px;
  margin: 0 0 8px;
  color: var(--primary);
  font-weight: 700;
  letter-spacing: -0.5px;
}
.cover-loan-id {
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 24px;
}

.cover-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 28px;
}
.cover-card {
  background: var(--light);
  padding: 10px 12px;
  border-radius: 4px;
  border: 1px solid var(--border);
}
.card-label {
  font-size: 10px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.card-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}

/* ---------- Score block ---------- */
.score-block {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 24px 32px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.tier-green { background: var(--green-bg); border: 2px solid #047857; }
.tier-amber { background: var(--amber-bg); border: 2px solid #d97706; }
.tier-red   { background: var(--red-bg);   border: 2px solid #b91c1c; }

.score-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 5px solid;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}
.tier-green .score-circle { border-color: #047857; color: #047857; }
.tier-amber .score-circle { border-color: #d97706; color: #92400e; }
.tier-red .score-circle   { border-color: #b91c1c; color: #991b1b; }
.score-value { font-size: 44px; font-weight: 700; line-height: 1; }
.score-max { font-size: 11px; opacity: 0.7; margin-top: 4px; }

.score-side { flex: 1; }
.score-heading {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
}
.tier-green .score-heading { color: #047857; }
.tier-amber .score-heading { color: #92400e; }
.tier-red .score-heading   { color: #991b1b; }

.score-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.stat-chip {
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: white;
}
.chip-green { color: #047857; }
.chip-cyan  { color: #0e7490; }
.chip-red   { color: #991b1b; }
.chip-amber { color: #92400e; }

/* ---------- Verdict ---------- */
.verdict {
  padding: 14px 20px;
  border-radius: 6px;
  margin-bottom: 18px;
  border-left: 5px solid;
}
.verdict-green { background: var(--green-bg); border-color: #047857; }
.verdict-amber { background: var(--amber-bg); border-color: #d97706; }
.verdict-red   { background: var(--red-bg);   border-color: #b91c1c; }
.verdict-label {
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 4px;
}
.verdict-green .verdict-label { color: #047857; }
.verdict-amber .verdict-label { color: #92400e; }
.verdict-red   .verdict-label { color: #991b1b; }
.verdict-text { font-size: 15px; font-weight: 600; color: var(--text); }

.meta-row {
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
  font-size: 11.5px;
  color: var(--muted);
  border-top: 1px solid var(--border);
  padding-top: 10px;
}
.meta-label { font-weight: 600; color: var(--text); }

/* ---------- Sections ---------- */
.section {
  margin-top: 28px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}
.section h2 {
  font-size: 20px;
  color: var(--primary);
  margin: 0 0 12px;
  font-weight: 700;
}
.section h3 {
  font-size: 14px;
  color: var(--text);
  margin: 20px 0 8px;
  font-weight: 600;
}
.section-intro {
  color: var(--muted);
  margin-bottom: 14px;
  font-size: 12px;
}
.empty-state {
  background: var(--light);
  border: 1px dashed var(--border);
  padding: 16px;
  border-radius: 6px;
  text-align: center;
  color: var(--muted);
  font-size: 13px;
}
.empty-warning { background: var(--amber-bg); border-color: #d97706; color: #92400e; }

/* ---------- Checklist table ---------- */
.checklist-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.checklist-table th {
  text-align: left;
  background: var(--primary);
  color: white;
  font-weight: 600;
  padding: 8px 10px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.checklist-table td {
  border-bottom: 1px solid var(--border);
  padding: 9px 10px;
  vertical-align: top;
}
.checklist-table tbody tr:nth-child(even) { background: #fafbfc; }
.point-num {
  font-weight: 700;
  text-align: center;
  color: var(--muted);
  font-family: 'SF Mono', Monaco, monospace;
}
.check-label { font-weight: 600; }
.check-desc { color: var(--muted); font-size: 11.5px; margin-top: 2px; }
.severity-cell { font-weight: 600; font-size: 11.5px; }
.los-path { font-size: 11px; color: var(--muted); }

.row-failed { background: #fef2f2 !important; }
.row-justified { background: #ecfeff !important; }
.row-inconclusive { background: #fffbeb !important; }
.row-no_los { background: #f8fafc !important; opacity: 0.85; }

/* ---------- Badges ---------- */
.badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.badge-green { background: var(--green-bg); color: var(--green-text); }
.badge-cyan  { background: var(--cyan-bg);  color: var(--cyan-text); }
.badge-red   { background: var(--red-bg);   color: var(--red-text); }
.badge-amber { background: var(--amber-bg); color: var(--amber-text); }
.badge-grey  { background: var(--grey-bg);  color: var(--grey-text); }

/* ---------- Findings ---------- */
.finding {
  border: 1px solid var(--border);
  border-left: 4px solid #b91c1c;
  border-radius: 6px;
  margin-bottom: 14px;
  page-break-inside: avoid;
}
.finding-header {
  background: #fef2f2;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
}
.finding-point {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.finding-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  margin: 2px 0 4px;
}
.finding-meta { display: flex; gap: 10px; align-items: center; font-size: 11px; }
.rule-id { font-family: 'SF Mono', Monaco, monospace; color: var(--muted); }
.finding-body { padding: 12px 14px; }
.finding-section { margin-bottom: 10px; font-size: 12px; }
.finding-section-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--muted);
  font-weight: 700;
  margin-bottom: 4px;
}
.los-path-inline { font-family: 'SF Mono', Monaco, monospace; font-size: 11px; }

/* ---------- KV tables for evidence ---------- */
.kv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-top: 4px;
}
.kv-table th, .kv-table td {
  padding: 5px 8px;
  border-bottom: 1px solid var(--border);
  text-align: left;
  vertical-align: top;
}
.kv-table th { background: var(--light); font-weight: 600; font-size: 10px; text-transform: uppercase; }
.mono { font-family: 'SF Mono', Monaco, monospace; }
.pre { white-space: pre-wrap; word-break: break-word; }

/* ---------- Workflow tiles ---------- */
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.workflow-tile {
  background: var(--light);
  border: 1px solid var(--border);
  padding: 10px 12px;
  border-radius: 4px;
}
.tile-label {
  font-size: 10px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.tile-value { font-size: 14px; font-weight: 600; color: var(--text); }
.tile-sub { font-size: 11px; color: var(--muted); margin-top: 2px; }

.conditions {
  margin: 8px 0;
  padding-left: 20px;
}
.conditions li { margin-bottom: 4px; font-size: 12px; }

/* ---------- Footer ---------- */
.report-footer {
  margin-top: 40px;
  padding-top: 14px;
  border-top: 2px solid var(--primary);
  font-size: 11px;
  color: var(--muted);
  text-align: center;
}
.report-footer .small { font-size: 10px; margin-top: 4px; }

/* ============================================================
   PRINT STYLES
   ============================================================ */
@media print {
  body { background: white; font-size: 11.5px; }
  .no-print { display: none !important; }
  .report {
    max-width: 100%;
    margin: 0;
    padding: 0;
    box-shadow: none;
  }
  .section { page-break-inside: avoid; }
  .page-break { page-break-before: always; }
  .finding { page-break-inside: avoid; }
  .checklist-table { font-size: 10.5px; }
  .checklist-table th { font-size: 9.5px; }
  .cover-title { font-size: 24px; }
  .score-block { padding: 14px 20px; }
  .score-circle { width: 90px; height: 90px; border-width: 4px; }
  .score-value { font-size: 32px; }
  @page { margin: 14mm 14mm; size: A4; }
}

/* ==========================================================================
   V2 (CAM-vs-LOS) REPORT STYLES
   ==========================================================================
   These classes are emitted by frontend/src/routes/los/cam-audit-report.ts.
   The V1 audit-report.ts uses the styles above; V2 uses the styles below.
   Both coexist because both reports share the same CSS bundle.
   ========================================================================== */

/* ---------- V2 Toolbar (sticky top bar, screen only) ---------- */
.toolbar-inner {
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}
.toolbar-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: white;
}
.btn-secondary {
  background: rgba(255,255,255,0.12);
  color: white;
  border-color: rgba(255,255,255,0.3);
}
.btn-secondary:hover { background: rgba(255,255,255,0.22); }

/* ---------- V2 Cover page (header + 3-column grid) ---------- */
.cover {
  margin-bottom: 32px;
}
.cover-header {
  border-bottom: 3px solid var(--primary);
  padding-bottom: 16px;
  margin-bottom: 24px;
}
.cover-brand {
  font-size: 11px;
  letter-spacing: 2px;
  color: var(--primary);
  font-weight: 700;
  text-transform: uppercase;
}
.cover-title {
  font-size: 28px;
  margin: 6px 0 4px;
  color: var(--primary);
  font-weight: 700;
  letter-spacing: -0.4px;
  line-height: 1.15;
}
.cover-subtitle {
  font-size: 13px;
  color: var(--muted);
  font-family: 'SF Mono', Monaco, monospace;
}

/* Two-column layout: loan summary on left, score+verdict on right.
   Each side gets equal weight so the page feels balanced. */
.cover-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 20px;
  margin-bottom: 24px;
  align-items: start;
}
.cover-meta h3 {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.cover-meta-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.cover-meta-table th {
  text-align: left;
  font-weight: 600;
  color: var(--muted);
  padding: 5px 12px 5px 0;
  vertical-align: top;
  width: 38%;
  font-size: 11.5px;
}
.cover-meta-table td {
  padding: 5px 0;
  vertical-align: top;
  color: var(--text);
  font-size: 12.5px;
}

/* Right side — score card */
.cover-score {
  background: var(--light);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 18px 18px 16px;
  text-align: center;
}
.score-ring {
  width: 110px;
  height: 110px;
  margin: 0 auto 12px;
  border-radius: 50%;
  border: 6px solid;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.score-ring.score-green { border-color: #047857; color: #047857; }
.score-ring.score-amber { border-color: #d97706; color: #92400e; }
.score-ring.score-red   { border-color: #b91c1c; color: #991b1b; }
.score-ring .score-value { font-size: 38px; font-weight: 700; line-height: 1; }
.score-ring .score-suffix { font-size: 10px; opacity: 0.75; margin-top: 2px; letter-spacing: 0.5px; }

.verdict {
  padding: 9px 12px;
  border-radius: 5px;
  margin-bottom: 12px;
  font-size: 12.5px;
  font-weight: 600;
  text-align: center;
  border-left: 4px solid;
}
.verdict-green { background: var(--green-bg); border-color: #047857; color: var(--green-text); }
.verdict-amber { background: var(--amber-bg); border-color: #d97706; color: var(--amber-text); }
.verdict-red   { background: var(--red-bg);   border-color: #b91c1c; color: var(--red-text); }

.score-tally {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 10px;
  font-size: 11.5px;
  color: var(--muted);
}
.score-tally > div {
  background: white;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 3px 10px;
}
.score-tally .t-passed { color: #047857; font-weight: 700; }
.score-tally .t-failed { color: #991b1b; font-weight: 700; }
.score-tally .t-inconclusive { color: #92400e; font-weight: 700; }
.score-tally .t-noLos  { color: #475569; font-weight: 700; }

/* Documents card (sits below the grid, full width) */
.cover-docs {
  background: var(--light);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 14px 18px;
  margin-bottom: 16px;
}
.cover-docs h3 {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 8px;
  padding-bottom: 0;
  border-bottom: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cover-foot {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
  font-size: 11px;
  color: var(--muted);
  text-align: center;
}

/* Small helper colors for inline text */
.dim  { color: var(--muted); }
.warn { color: #b91c1c; font-weight: 600; }

/* ---------- V2 Checklist table ---------- */
.checklist {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 16px;
}
.checklist thead th {
  background: var(--primary);
  color: white;
  font-weight: 600;
  text-align: left;
  padding: 9px 12px;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.checklist tbody td {
  border-bottom: 1px solid var(--border);
  padding: 11px 12px;
  vertical-align: top;
}
.checklist tbody tr:nth-child(even) { background: #fafbfc; }
.checklist tbody tr:hover { background: #f1f5f9; }
/* Match actual class names emitted by renderChecklistRow */
.checklist .c-pt {
  font-family: 'SF Mono', Monaco, monospace;
  font-weight: 700;
  color: var(--muted);
  text-align: center;
  width: 36px;
}
.checklist .c-status { width: 120px; }
.checklist .c-sev    { width: 80px; font-weight: 600; font-size: 11.5px; }
.checklist .check-label { font-weight: 600; color: var(--text); font-size: 12.5px; }
.checklist .check-desc  { color: var(--muted); font-size: 11px; margin-top: 2px; line-height: 1.4; }
.checklist .check-note  { line-height: 1.5; }
/* Severity colors via the c-sev cell content (renderer puts severity text directly) */
.checklist tr.row-s-failed       .c-sev { color: #991b1b; }
.checklist tr.row-s-inconclusive .c-sev { color: #92400e; }
/* Subtle row backgrounds by status, much lighter than the full-color row tint */
.checklist tr.row-s-failed > td       { background: rgba(239, 68, 68, 0.04); }
.checklist tr.row-s-inconclusive > td { background: rgba(217, 119, 6, 0.05); }
.checklist tr.row-s-noLos > td        { color: var(--muted); }

/* Badges in checklist (renderer uses class="badge s-passed" etc) */
.badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.2px;
}
.badge.s-passed       { background: var(--green-bg); color: var(--green-text); }
.badge.s-failed       { background: var(--red-bg);   color: var(--red-text); }
.badge.s-inconclusive { background: var(--amber-bg); color: var(--amber-text); }
.badge.s-noLos        { background: var(--grey-bg);  color: var(--grey-text); }

/* ---------- V2 Mismatch detail (finding cards) ---------- */
.finding-card {
  border: 1px solid var(--border);
  border-left: 4px solid #b91c1c;
  border-radius: 6px;
  margin-bottom: 14px;
  page-break-inside: avoid;
  overflow: hidden;
  background: white;
}
.finding-card.finding-card-amber {
  border-left-color: #d97706;
}
.finding-header {
  background: #fef2f2;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.finding-card.finding-card-amber .finding-header { background: #fffbeb; }

.finding-point {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: var(--muted);
  background: white;
  border: 1px solid var(--border);
  border-radius: 3px;
  padding: 2px 7px;
  text-transform: uppercase;
}
.finding-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--text);
  flex: 1;
  min-width: 0;
}
.finding-sev {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.finding-sev.sev-high   { color: #991b1b; }
.finding-sev.sev-medium { color: #92400e; }
.finding-sev.sev-low    { color: #047857; }

.finding-desc {
  padding: 10px 14px 4px;
  color: var(--text);
  font-size: 12.5px;
  line-height: 1.5;
}
.finding-source {
  padding: 8px 14px 12px;
  font-size: 11.5px;
  color: var(--muted);
  border-top: 1px dashed var(--border);
  margin-top: 8px;
}
.finding-source code {
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 11px;
  background: var(--light);
  padding: 1px 6px;
  border-radius: 3px;
}

/* Generic OK message banner */
.ok-msg {
  background: var(--green-bg);
  color: var(--green-text);
  border-left: 4px solid #047857;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
}

/* ---------- V2 Comparison tables (LOS vs CAM) ---------- */
.compare {
  width: 100%;
  border-collapse: collapse;
  font-size: 11.5px;
  margin: 8px 0;
}
.compare thead th {
  background: var(--light);
  text-align: left;
  font-weight: 600;
  padding: 7px 10px;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
}
.compare tbody td {
  padding: 7px 10px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}
.compare tbody tr:last-child td { border-bottom: none; }
.compare tbody td:first-child { font-weight: 600; color: var(--text); width: 28%; }
.compare tbody td.num { font-family: 'SF Mono', Monaco, monospace; font-size: 11px; }
.compare tbody td.c-status { text-align: center; width: 90px; }

/* ---------- V2 Section base ---------- */
.section h2 {
  font-size: 18px;
  color: var(--primary);
  margin: 0 0 10px;
  font-weight: 700;
  letter-spacing: -0.2px;
}
.section h3 {
  font-size: 13px;
  color: var(--text);
  margin: 16px 0 6px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ---------- V2 CAM Extracted Fields ---------- */
.extracted {
  width: 100%;
  border-collapse: collapse;
  font-size: 11.5px;
  margin-bottom: 12px;
}
.extracted thead th {
  background: var(--light);
  font-weight: 600;
  text-align: left;
  padding: 6px 10px;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
}
.extracted tbody th {
  text-align: left;
  font-weight: 600;
  padding: 6px 10px;
  color: var(--text);
  width: 38%;
  vertical-align: top;
  border-bottom: 1px solid #e2e8f0;
}
.extracted tbody td {
  padding: 6px 10px;
  vertical-align: top;
  border-bottom: 1px solid #e2e8f0;
  word-break: break-word;
}
.extracted tbody tr:nth-child(even) { background: #fafbfc; }
.extracted .num { text-align: right; font-family: 'SF Mono', Monaco, monospace; color: var(--muted); width: 80px; }

/* ---------- V2 Footer ---------- */
.footer {
  margin-top: 32px;
  padding-top: 16px;
  border-top: 2px solid var(--primary);
  font-size: 11px;
  color: var(--muted);
  text-align: center;
  line-height: 1.6;
}
.footer .signoff {
  margin-top: 20px;
  display: flex;
  justify-content: space-around;
  font-size: 11.5px;
  color: var(--text);
}
.signoff-line { font-family: 'SF Mono', Monaco, monospace; }

/* ---------- V2 Print refinements ---------- */
@media print {
  .cover-grid { grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); }
  .score-ring { width: 86px; height: 86px; border-width: 4px; }
  .score-ring .score-value { font-size: 30px; }
  .cover-title { font-size: 22px; }
  .checklist { font-size: 10.5px; }
  .checklist tbody td { padding: 7px 8px; }
  .checklist thead th { font-size: 9.5px; padding: 7px 8px; }
  .compare { font-size: 10.5px; }
  .extracted { font-size: 10.5px; }
  .mismatch-card { page-break-inside: avoid; }
  .section { page-break-inside: avoid; }
  .toolbar { display: none !important; }
}
`;

// -------------------------------------------------------------------------
// Inline JS for toolbar actions
// -------------------------------------------------------------------------

export const REPORT_JS = `
document.getElementById('btn-print')?.addEventListener('click', () => {
  window.print();
});
document.getElementById('btn-download-html')?.addEventListener('click', () => {
  // Build a clean copy that omits the toolbar (no_print class).
  const clone = document.documentElement.cloneNode(true);
  clone.querySelectorAll('.no-print').forEach((n) => n.remove());
  const html = '<!DOCTYPE html>' + clone.outerHTML;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'audit-report-' + (document.title.split('— ')[1] || 'loan') + '.html';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
});
document.getElementById('btn-close')?.addEventListener('click', () => {
  window.close();
});
`;
